import { useCallback, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, useFetch } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader, Loading, ErrorBox, EmptyState, Section, StatusBadge, Field, NativeSelect } from "@/components/Common";
import { SlotPicker } from "@/components/SlotPicker";

const EXPLAIN = {
  Requested: "Requested - waiting for DJ Landscapes to confirm. This is not yet confirmed.",
  Confirmed: "Confirmed - we look forward to speaking with you.",
  "Rescheduling Requested": "DJ Landscapes has suggested a different time. Please accept it or cancel.",
  Completed: "This appointment has taken place.",
  Cancelled: "This appointment was cancelled.",
  Declined: "DJ Landscapes could not accept this request. You can request another time.",
};

export function Appointments() {
  const { data, loading, error, reload } = useFetch("/customer/appointments");
  const act = async (a, path, msg) => {
    try { await api.post(`/customer/appointments/${a.id}/${path}`); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (loading) return <Loading />;
  return (
    <div>
      <PageHeader title="Appointments" actions={<Button asChild data-testid="book-appt-btn"><Link to="/portal/appointments/book">Request an appointment</Link></Button>} />
      <ErrorBox error={error} />
      {!data?.length ? <EmptyState title="No appointments yet" text="Request a telephone, video or on-site consultation for any of your job requests." /> : (
        <div className="space-y-3">
          {data.map((a) => (
            <Section key={a.id} testId={`appt-card-${a.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium">{a.type} · {fmtDate(a.date)} at {a.start}–{a.end}</p>
                  <p className="text-sm text-muted-foreground">Request {a.request_reference}</p>
                  <p className="mt-2 text-sm" data-testid="appt-explain">{EXPLAIN[a.status]}</p>
                  {a.suggested && <p className="mt-1 text-sm font-medium" data-testid="appt-suggested">Suggested time: {fmtDate(a.suggested.date)} at {a.suggested.start}</p>}
                  {a.admin_message && <p className="mt-1 rounded bg-muted p-2 text-sm">Message: {a.admin_message}</p>}
                </div>
                <StatusBadge status={a.status} testId="appt-status" />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {a.status === "Rescheduling Requested" && a.suggested && <Button size="sm" onClick={() => act(a, "accept-suggestion", "Suggested time accepted - your appointment is confirmed")} data-testid="appt-accept-suggestion-btn">Accept suggested time</Button>}
                {["Requested", "Confirmed", "Rescheduling Requested"].includes(a.status) && <Button size="sm" variant="outline" onClick={() => window.confirm("Cancel this appointment?") && act(a, "cancel", "Appointment cancelled")} data-testid="appt-cancel-btn">Cancel</Button>}
              </div>
            </Section>
          ))}
        </div>
      )}
    </div>
  );
}

export function BookAppointment() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { data: reqs } = useFetch("/customer/requests");
  const { data: cfg } = useFetch("/availability/config");
  const [f, setF] = useState({ request_id: params.get("request") || "", type: "", notes: "" });
  const [slot, setSlot] = useState({ date: null, start: null });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const slotsUrl = useCallback((d) => `/availability/slots?date=${d}`, []);
  if (!reqs || !cfg) return <Loading />;
  const open = reqs.filter((r) => !["Declined", "Closed", "Converted to Job"].includes(r.status));
  if (!open.length) return <EmptyState title="No open job requests" text="Submit a job request first, then you can request a consultation." action={<Button asChild><Link to="/portal/new">New job request</Link></Button>} />;
  const submit = async () => {
    setErr(null);
    if (!f.request_id || !f.type || !slot.date || !slot.start) return setErr("Please choose a job request, appointment type, date and time.");
    setBusy(true);
    try {
      await api.post("/customer/appointments", { ...f, date: slot.date, start: slot.start });
      toast.success("Appointment requested. It is NOT confirmed until DJ Landscapes confirms it.");
      nav("/portal/appointments");
    } catch (e) { setErr(errMsg(e)); setSlot({ ...slot, start: null }); } finally { setBusy(false); }
  };
  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="Request an appointment" subtitle="Choose a preferred time. DJ Landscapes will confirm or suggest an alternative." />
      <Section>
        <div className="grid gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Job request" id="ar" required>
              <NativeSelect id="ar" value={f.request_id} onChange={(e) => setF({ ...f, request_id: e.target.value })} data-testid="appt-request-select">
                <option value="">Select…</option>{open.map((r) => <option key={r.id} value={r.id}>{r.reference} - {r.services.map((s) => s.name).join(", ")}</option>)}
              </NativeSelect>
            </Field>
            <Field label="Appointment type" id="at" required>
              <NativeSelect id="at" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="appt-type-select">
                <option value="">Select…</option>{cfg.appointment_types.map((t) => <option key={t}>{t}</option>)}
              </NativeSelect>
            </Field>
          </div>
          <div><p className="mb-2 text-sm font-medium">Preferred date and time <span className="text-xs text-muted-foreground">({cfg.duration} minutes)</span></p>
            <SlotPicker config={cfg} slotsUrl={slotsUrl} value={slot} onChange={setSlot} /></div>
          <Field label="Additional notes (optional)" id="an"><Textarea id="an" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="appt-notes-input" /></Field>
          <ErrorBox error={err} />
          <Button onClick={submit} disabled={busy} data-testid="appt-submit-btn">{busy ? "Sending…" : "Request appointment"}</Button>
        </div>
      </Section>
    </div>
  );
}
